/**
 * @ngdoc directive
 * @module app
 * @name app:videoManager
 * @scope
 * @restrict E
 * @description
 * Factory used to create video provider instances. Used to generalize video event handling so that the same methods can be used to control multiple video services. All providers must have the following
 * - init: Initializes the video controls, where the id is the id on the player element.
 * - play: Plays the video.
 * - pause: Pauses the video.
 * - stop: Stops the video (for services that don't directly support this, the video should be paused and time should be set back to the start).
 * There are also a couple optional settings.
 * - callback: Name of the callback function that is triggered when the video service javascript api is ready.
 * - scripts: Scripts needed to enable javascript control of the video.
 */
export default function VideoProvidersFactory(moduleName) {
    angular.module(moduleName || 'app')
        .factory('videoProviders', videoProviders);

    videoProviders.$inject = ['$window', '$q'];
    function videoProviders($window, $q) {
        var queue = {};
        var providerPromises = {};
        var providers = {
            YouTube: {
                callback: 'onYouTubeIframeAPIReady',
                scripts: [
                    'https://www.youtube.com/iframe_api'
                ],
                init: function (id, loop) {
                    var deferred = $q.defer();
                    var self = this;
                    self.loop = loop;
                    providerPromises['YouTube'].then(function () {
                        self.player = new YT.Player(id, {
                            events: {
                                'onReady': function () {
                                    self.ready = true;
                                    deferred.resolve();
                                },
                                onStateChange: function (event) {
                                    if (self.loop && event.data === YT.PlayerState.ENDED) {
                                        self.play();
                                    }
                                }
                            }
                        });
                    });
                    return deferred.promise;
                },
                play: function () {
                    this.player.playVideo();
                },
                pause: function () {
                    this.player.pauseVideo();
                },
                stop: function () {
                    this.player.stopVideo();
                }
            },
            Vimeo: {
                callback: 'vimeoPlayerReady',
                init: function (id) {
                    var deferred = $q.defer();
                    var self = this;
                    providerPromises['Vimeo'].then(function () {
                        var iframe = angular.element('#' + id);
                        if (!iframe.is('iframe')) {
                            iframe = iframe.find('iframe');
                        }
                        self.player = new Vimeo.Player(iframe[0]);
                        self.player.on('loaded', function () {
                            self.ready = true;
                            deferred.resolve();
                        });
                    });
                    return deferred.promise;
                },
                play: function () {
                    this.player.play();
                },
                pause: function () {
                    this.player.pause();
                },
                stop: function () {
                    this.player.pause();
                    this.player.setCurrentTime(0);
                }
            },
            'Wistia, Inc.': {
                scripts: [
                    '//fast.wistia.com/assets/external/E-v1.js'
                ],
                init: function (id) {
                    var deferred = $q.defer();
                    var self = this;
                    var vid = angular.element('#' + id);
                    var classes = vid.attr('class');
                    var idExtractor = /wistia_async_(\w+)/;
                    var videoId = idExtractor.exec(classes)[1];
                    $window._wq = $window._wq || [];
                    _wq.push({
                        id: videoId,
                        onEmbedded: function (video) {
                            self.ready = true;
                            self.player = video;
                            deferred.resolve();
                        }
                    });
                    return deferred.promise;
                },
                play: function () {
                    this.player.play();
                },
                pause: function () {
                    this.player.pause();
                },
                stop: function () {
                    this.player.pause();
                    this.player.time(0);
                }
            }
        };

        return {
            /**
             * @ngdoc method
             * @name app.videoProviders#get
             * @description
             * Creates an instance of the video provider and loads any required scripts.
             * @param {string} provider Name of the video service to enable javascript controls for.
             * @returns {object} Service that can used to control the video.
             */
            get: function (provider) {
                if (!providers[provider]) {
                    return null;
                }
                var prov = providers[provider];
                if (!providerPromises[provider]) {
                    var deferred = $q.defer();
                    if (prov.callback) {
                        $window[prov.callback] = function () {
                            deferred.resolve();
                        };
                        if (prov.scripts) {
                            var firstScriptTag = document.getElementsByTagName('script')[0];
                            for (var i = 0; i < prov.scripts.length; i++) {
                                var tag = document.createElement('script');
                                document.defer = true;
                                tag.src = prov.scripts[i];
                                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
                            }
                        }
                    }
                    else {
                        deferred.resolve();
                    }
                    providerPromises[provider] = deferred.promise;
                }
                return angular.copy(providers[provider]);
            }
        }
    }
};